import { inject, Service } from '@angular/core';
import type { PostHogConfig } from 'posthog-js';
import { POSTHOG_CLIENT } from './posthog-client';

/**
 * Thin wrapper around the PostHog browser SDK for product analytics,
 * error capture, and performance events. Replaceable behind this facade
 * if the vendor changes.
 */
@Service()
export class AnalyticsService {
  private readonly posthog = inject(POSTHOG_CLIENT);

  private initialized = false;

  /** Captures a custom analytics event. No-ops when analytics is disabled. */
  capture(event: string, properties?: Record<string, unknown>): void {
    if (!this.initialized) {
      return;
    }

    this.posthog.capture(event, properties);
  }

  /** Associates subsequent events with a stable user id (never email). */
  identifyUser(userId: string, traits?: Record<string, unknown>): void {
    if (!this.initialized) {
      return;
    }

    this.posthog.identify(userId, traits);
  }

  /** Initializes PostHog when analytics is enabled for the current build. */
  init(key: string, options?: Partial<PostHogConfig>): void {
    this.posthog.init(key, options);
    this.initialized = true;
  }

  /** Returns whether PostHog has been initialized for this session. */
  isInitialized(): boolean {
    return this.initialized;
  }

  /** Clears the current user identity after logout. */
  reset(): void {
    if (!this.initialized) {
      return;
    }

    this.posthog.reset();
  }
}
